import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { useSimpleToast, getErrorMessage } from '../components/Toast';
import { Clock, Save } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AttendanceConfig({ user, onLogout }) {
  const { showToast, ToastComponent } = useSimpleToast();
  const [config, setConfig] = useState({
    grace_period_minutes: 15,
    grace_allowed_per_month: 3,
    full_day_hours: 8,
    half_day_hours: 4,
    week_off: 'Sunday',
    overtime_allowed: false,
    late_marks_for_half_day_lop: 3
  });
  const [loading, setLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/hr-config/attendance-rules`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfig(response.data);
    } catch (error) {
      console.error('Error fetching config:', error);
    }
  };

  const handleChange = (field, value) => {
    setConfig({ ...config, [field]: value });
    setHasChanges(true);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/hr-config/attendance-rules`, config, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showToast('Attendance rules updated successfully!', 'success');
      setHasChanges(false);
      fetchConfig();
    } catch (error) {
      console.error('Save config error:', error);
      showToast(getErrorMessage(error), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    fetchConfig();
    setHasChanges(false);
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <ToastComponent />
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-3xl font-bold" style={{color: '#2b3380'}}>Attendance Rules Configuration</h1>
          <p className="text-gray-600 mt-2">Configure attendance policies, grace periods, and working hours</p>
        </div>

        {/* Info Card */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h3 className="font-semibold text-blue-900 mb-2">⏰ Attendance Governance</h3>
          <p className="text-sm text-blue-800">
            Set up attendance rules that apply to all employees. Changes affect how attendance is marked and calculated across the organization.
          </p>
        </div>

        {/* Configuration Form */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Grace Period */}
            <div>
              <label className="block text-sm font-medium mb-2">Grace Period (Minutes)</label>
              <input
                type="number"
                value={config.grace_period_minutes}
                onChange={(e) => handleChange('grace_period_minutes', parseInt(e.target.value))}
                className="w-full px-3 py-2 border rounded-lg"
              />
              <p className="text-xs text-gray-500 mt-1">Late arrivals within this time won't be marked</p>
            </div>

            {/* Grace Allowed Per Month */}
            <div>
              <label className="block text-sm font-medium mb-2">Grace Allowed Per Month</label>
              <input
                type="number"
                value={config.grace_allowed_per_month}
                onChange={(e) => handleChange('grace_allowed_per_month', parseInt(e.target.value))}
                className="w-full px-3 py-2 border rounded-lg"
              />
              <p className="text-xs text-gray-500 mt-1">Number of times grace period can be used per month</p>
            </div>

            {/* Full Day Hours */}
            <div>
              <label className="block text-sm font-medium mb-2">Full Day Hours</label>
              <input
                type="number"
                value={config.full_day_hours}
                onChange={(e) => handleChange('full_day_hours', parseInt(e.target.value))}
                className="w-full px-3 py-2 border rounded-lg"
              />
              <p className="text-xs text-gray-500 mt-1">Minimum hours for full day attendance</p>
            </div>

            {/* Half Day Hours */}
            <div>
              <label className="block text-sm font-medium mb-2">Half Day Hours</label>
              <input
                type="number"
                value={config.half_day_hours}
                onChange={(e) => handleChange('half_day_hours', parseInt(e.target.value))}
                className="w-full px-3 py-2 border rounded-lg"
              />
              <p className="text-xs text-gray-500 mt-1">Minimum hours for half day attendance</p>
            </div>

            {/* Week Off */}
            <div>
              <label className="block text-sm font-medium mb-2">Week Off Day</label>
              <select
                value={config.week_off}
                onChange={(e) => handleChange('week_off', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="Monday">Monday</option>
                <option value="Tuesday">Tuesday</option>
                <option value="Wednesday">Wednesday</option>
                <option value="Thursday">Thursday</option>
                <option value="Friday">Friday</option>
                <option value="Saturday">Saturday</option>
                <option value="Sunday">Sunday</option>
              </select>
            </div>

            {/* Late Marks for Half Day LOP */}
            <div>
              <label className="block text-sm font-medium mb-2">Late Marks for Half Day LOP</label>
              <input
                type="number"
                value={config.late_marks_for_half_day_lop}
                onChange={(e) => handleChange('late_marks_for_half_day_lop', parseInt(e.target.value))}
                className="w-full px-3 py-2 border rounded-lg"
              />
              <p className="text-xs text-gray-500 mt-1">Number of late marks before half day LOP is applied</p>
            </div>

            {/* Overtime Allowed */}
            <div className="md:col-span-2">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={config.overtime_allowed}
                  onChange={(e) => handleChange('overtime_allowed', e.target.checked)}
                  className="w-4 h-4"
                />
                <span className="text-sm font-medium">Allow Overtime</span>
              </label>
              <p className="text-xs text-gray-500 mt-1 ml-6">Enable overtime tracking for employees</p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 mt-6 pt-6 border-t">
            <button
              onClick={handleSave}
              disabled={!hasChanges || loading}
              className="px-6 py-3 rounded-lg text-white font-medium flex items-center gap-2"
              style={{backgroundColor: hasChanges ? '#2b3380' : '#9e9e9e'}}
            >
              <Save className="w-5 h-5" />
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
            <button
              onClick={handleReset}
              disabled={!hasChanges}
              className="px-6 py-3 rounded-lg border font-medium"
            >
              Reset
            </button>
          </div>

          {hasChanges && (
            <p className="text-sm text-orange-600 mt-3">⚠️ You have unsaved changes</p>
          )}
        </div>

        {/* Current Rules Summary */}
        <div className="bg-white rounded-lg shadow-md p-6 mt-6">
          <h2 className="text-xl font-bold mb-4" style={{color: '#2b3380'}}>Current Rules Summary</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Grace Period</p>
              <p className="text-2xl font-bold" style={{color: '#2b3380'}}>{config.grace_period_minutes} min</p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Full Day</p>
              <p className="text-2xl font-bold text-green-700">{config.full_day_hours} hrs</p>
            </div>
            <div className="bg-orange-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Half Day</p>
              <p className="text-2xl font-bold text-orange-700">{config.half_day_hours} hrs</p>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Week Off</p>
              <p className="text-2xl font-bold text-purple-700">{config.week_off}</p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default AttendanceConfig;